import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * NativeSelect - Custom dropdown built with div/button elements
 * AVOIDS native <select> because the Emergent platform injects <span> elements
 * inside <select>/<option>, which breaks mobile browsers (iOS Safari, etc.)
 * that strictly enforce HTML spec (only <option>/<optgroup> inside <select>).
 */
export function NativeSelect({
  value,
  onValueChange,
  placeholder = "Selecione...",
  options = [],
  disabled = false,
  loading = false,
  searchable = false,
  className = "",
  id,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [q, setQ] = useState("");
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const isDisabled = disabled || loading;
  const displayPlaceholder = loading ? "Carregando..." : placeholder;

  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption?.label || displayPlaceholder;
  const hasValue = !!selectedOption;

  // Busca sem acento/maiusculas; aparece quando a lista tem mais de 5 opcoes
  const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const pesquisavel = searchable || options.length > 5;
  const visiveis = q.trim() ? options.filter((o) => norm(o.label).includes(norm(q))) : options;

  useEffect(() => {
    if (isOpen) {
      setQ("");
      const t = setTimeout(() => searchRef.current?.focus({ preventScroll: true }), 60);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Close on outside click/touch
  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  // Scroll selected into view when opening
  useEffect(() => {
    if (isOpen && listRef.current && value) {
      const selected = listRef.current.querySelector('[data-selected="true"]');
      if (selected) selected.scrollIntoView({ block: "nearest" });
    }
  }, [isOpen, value]);

  const handleSelect = (optionValue) => {
    onValueChange(optionValue);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!isDisabled) setIsOpen((prev) => !prev);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (isDisabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    } else if (e.key === "ArrowDown" && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      {...props}
    >
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={isDisabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={cn(
          "neu-input w-full h-10 px-3 py-2 text-sm",
          "flex items-center justify-between gap-2 text-left",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !hasValue && "text-muted-foreground",
          loading && "animate-pulse"
        )}
      >
        <span className="truncate flex-1">{displayValue}</span>
        {loading ? (
          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin shrink-0" />
        ) : (
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        )}
      </button>

      {isOpen && options.length > 0 && (
        <div
          ref={listRef}
          role="listbox"
          className={cn(
            "absolute z-[9999] w-full mt-2 py-1 bg-neu-base rounded-neu-md shadow-neu-outset border border-white/40",
            "max-h-[240px] overflow-y-auto overscroll-contain",
            "animate-in fade-in-0 zoom-in-95 duration-100"
          )}
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {pesquisavel && (
            <div className="sticky top-0 bg-neu-base px-2 pt-1 pb-1.5 z-10">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Digite para filtrar…" data-testid="select-busca"
                  className="w-full h-8 pl-8 pr-2 text-sm rounded-md border border-slate-200 bg-white outline-none focus:border-luvcorp-gold"
                  onKeyDown={(e) => e.stopPropagation()} />
              </div>
            </div>
          )}
          {visiveis.length === 0 && (
            <div className="px-3 py-3 text-center text-sm text-muted-foreground">Nada encontrado para “{q}”</div>
          )}
          {visiveis.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected}
                onClick={() => handleSelect(option.value)}
                className={cn(
                  "w-full px-3 py-2.5 text-sm text-left rounded-neu-sm mx-1 my-0.5",
                  "flex items-center justify-between",
                  "hover:bg-luvcorp-gold/15 hover:text-luvcorp-navy",
                  "active:bg-luvcorp-gold/25",
                  "touch-manipulation transition-colors",
                  isSelected && "bg-luvcorp-gold/20 font-medium text-luvcorp-navy"
                )}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && <Check className="h-4 w-4 text-luvcorp-gold shrink-0 ml-2" />}
              </button>
            );
          })}
        </div>
      )}

      {isOpen && options.length === 0 && !loading && (
        <div className="absolute z-[9999] w-full mt-1 py-3 bg-popover border border-input rounded-md shadow-lg text-center text-sm text-muted-foreground">
          Nenhuma opção disponível
        </div>
      )}
    </div>
  );
}

export default NativeSelect;
